// ** React Imports
import { useEffect, useState } from 'react';
import { memo, useMemo } from 'react';
import { Fragment } from "react/jsx-runtime";
import { useLocation, useNavigate } from 'react-router-dom';

// ** MUI Imports
import {
    Avatar,
    Stepper,
    StepLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Paper,
    CardContent,
    Box,
    Grid,
    FormControl,
    FormLabel,
    RadioGroup,
    IconButton,
    Radio,
    FormControlLabel,
    ListItemText,
    ListItem,
    FormHelperText,
    Divider,
    InputAdornment,
    Card,
    ListItemIcon,
    Autocomplete,
    Checkbox,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Icon Imports
import Icon from "../../../components/icon";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import StarIcon from "@mui/icons-material/Star";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import StarOutlineIcon from "@mui/icons-material/StarOutline";

// ** Custom Component Imports
import StepperCustomDot from "../../../components/mui/StepperCustomDot";
import CustomAvatar from "../../../components/mui/avatar";
import StepperWrapper from "../../../components/styles/mui/stepper";
import CustomChip from "../../../components/mui/chip";
import CustomTextField from "../../../components/mui/text-field";
import CustomAutocomplete from "../../../components/mui/autocomplete";
import { expandStateName } from "../../../components/US_StatesNames";

// ** Hooks
import { hexToRGBA } from "../../../components/utils/hex-to-rgba";

// ** Component Imports
import QuickView from "./QuickView";
import AddContactDialog from "./AddContact";
import UpdateContactDialog from "./UpdateContactDialog";
import AddJobsiteDialog from "./AddJobsite";

// ** Third-Party Imports
import Draggable from "react-draggable";
import PhoneInput from "react-phone-input-2";

// ** Logic Imports
import { timezones } from "../../../components/US_Timezones";
import { stateNames } from "../../../components/US_StatesNames";
import { useCustomerLogic } from '../logic/CustomerLogic';
import { SaveBillingInfo } from '../logic/BillingInfoLogic'
import BillingInformation from './BillingInformation';
import BulkImport from "../../../components/BulkImport";
import { dev_baseUrl } from 'utils/var';

const DraggablePaper = memo(function DraggablePaper(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
});

const steps = [
  {
    icon: "tabler:user",
    title: "Profile Information",
  },
  {
    icon: "tabler:align-box-left-stretch",
    title: "Billing Information",
  },
  {
    icon: "tabler:address-book",
    title: "Add Contact",
  },
  {
    icon: "tabler:align-box-bottom-center",
    title: "Add Jobsite",
  },
];


const AddCustomerDialog = ({
  open,
  setOpenNewCustomerDlg,
  fetchData,  
  allCustomers,
}) => {
    // ** Hooks
    const theme = useTheme();

    // ** Constants
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isXSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // ** States
    const [activeStep, setActiveStep] = useState(0);
    const [isChanged, setIsChanged] = useState(false);
    const [billingInfoChanged, setBillingInfoChanged] = useState(false);

    const [globalContactsAll, setGlobalContactsAll] = useState([]);
    const [emailIndex, setEmailIndex] = useState(new Set());
    const [phoneIndex, setPhoneIndex] = useState(new Set());
    const [dupeErrors, setDupeErrors] = useState({
      customerEmail: "",
      customerPhones: {},     // { [idx]: "message" }
      contactEmail: "",
      contactPhones: {},      // { [idx]: "message" }
    });

    const {
      CustomerProps
    } = useCustomerLogic({activeStep, setActiveStep, steps, setOpenNewCustomerDlg,fetchData, allCustomers});

    const {
      NProgress,
      //StepperHeaderContainer,
      StepJobsite,
      columnsAddContact,
      quickViewOpen,
      contacts,
      jobsite,
      propertyAreaList,
      alertList,
      customer,
      errors, 
      isValidPhone, 
      phoneError,
      searchContactText,
      searchJobsiteText,
      filteredContactRows,
      paginationModelAddContact,
      allJobsites,
      //activeStep,
      openNewContactDlg,
      openUpdateContactDlg,
      toast,
      contactID,      
      openNewJobsiteDlg,
      setOpenNewJobsiteDlg,
      allContacts,
      propertyArea,
      areaMeasurement,
      activeStepJobsite,
      jobsiteContent,
      clearPropertyArea,
      fetchPropertyArea,
      clearAreaMeasurement,
      setActiveStepJobsite,
      setPropertyArea,
      setAreaMeasurement,
      navigate,
      handleOpenNewJobsiteDlg,
      setContactID,
      handleChange,
      setAlertList,
      handleQuickViewClose,
      handleJobsiteChange,
      handleJobsiteDelete,
      handleJobsiteEdit,
      handleQuickViewOpen,
      handlePhoneFieldChange,
      handleSetPrimary,
      handleDeletePhoneField,
      handleAddPhoneField,
      handleClearContactSearch,
      handleContactSearchChange,
      handleClearJobsiteSearch,
      handleJobsiteSearchChange,
      handleOpenNewContactDlg,
      handleCloseNewContactDlg,
      handleContactPaginationModel,
      handleCloseUpdateDlg,
      handleNext,
      handleBack, 
      setLoading,
      fetchContacts,  
      fetchJobsites, 
      handleCloseNewJobsiteDlg,
      files,
      setFiles,
      handleOpenBulkImportContactDlg,
      handleOpenBulkImportJobsiteDlg,
      contact,
      handleContactChange,
      errorContacts,
      handleContactPhoneFieldChange,
      handleSetPrimaryContact,
      handleDeletePhoneFieldContact,
      handleAddPhoneFieldContact,
      isValidContactPhone,
      contactPhoneError,
      handleClose,

      // Billing Info
      // estTravelTime,
      // setEstTravelTime,
      billingFreq,
      setBillingFreq,
      recurringType,
      setRecurringType,
      depositReq,
      setDepositReq,
      depositType,
      setDepositType,
      formatsBilling,
      setFormatsBilling,
      monthlyOptions,
      setMonthlyOptions,
      // occurrences, 
      // setOccurrences,
      cycleStartDate, 
      setCycleStartDate,
      intervalDaysInput, 
      setIntervalDaysInput,
      weekOfMonth, 
      setWeekOfMonth,
      quarter, 
      setQuarter,
      monthOfQuarter, 
      setMonthOfQuarter,
      // handleEstTravelTimeChange,
      handleBillingFreqChange,
      handleRecurringTypeChange,
      handledDepositReqChange,
      handleDepositTypeChange,
      handleFormatBilling,
      allEmployees,
      openBulkImportDlg,
      handleCloseBulkImportDlg,
      BulkImportContent,
      EmpFiles,
      btnLoader,
      getRootProps,
      getInputProps,
      setEmpFiles,
    } = CustomerProps;

    const location = useLocation();
// const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.reopenAddCustomer) {
      setOpenNewCustomerDlg(true);

      // If you can set the dialog step from parent:
      if (typeof setActiveStep === 'function' && Number.isInteger(location.state.reopenStep)) {
        setActiveStep(location.state.reopenStep);
      }

      // Clean the state so refresh/back doesn’t re-trigger
      navigate(location.pathname + location.search + location.hash, {
        replace: true,
        state: {}
      });
    }
  }, [location, navigate]);

    const sortedEmployees = useMemo(() => {
      return [...allEmployees].sort((a, b) =>
        `${a.firstName} ${a.lastName}`
          .toLowerCase()
          .localeCompare(`${b.firstName} ${b.lastName}`.toLowerCase())
      );
    }, [allEmployees]);

    const normalizeEmail = (e) => (e || "").trim().toLowerCase();
    const normalizePhone = (p) => (p || "").replace(/\D/g, "");

    // ADD: index lookups
    const isEmailTaken = (email) => {
      const e = normalizeEmail(email);
      if (!e) return false;
      return emailIndex.has(e);
    };
    const isPhoneTaken = (phone) => {
      const p = normalizePhone(phone);
      if (!p) return false;
      return phoneIndex.has(p);
    };

    const fetchAllContactsSystemWide = async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        if (!token) return;
    
        const res = await fetch(
          `${dev_baseUrl}/Contact/getContact/0/0`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        if (!json.isError) {
          setGlobalContactsAll(Array.isArray(json.result) ? json.result : []);
        } else {
          console.error('Failed to fetch all contacts:', json.message);
        }
      } catch (e) {
        console.error('Error fetching all contacts (system-wide):', e);
      }
    };

    useEffect(() => {
      if (open) fetchAllContactsSystemWide();
    }, [open]);

    useEffect(() => {
      const eSet = new Set();
      const pSet = new Set();
    
      // Customers
      (allCustomers || []).forEach(c => {
        if (c?.email) eSet.add(normalizeEmail(c.email));
        if (Array.isArray(c?.phoneNumbers)) {
          c.phoneNumbers.forEach(p => {
            if (p?.phoneNo) pSet.add(normalizePhone(p.phoneNo));
          });
        }
      });
    
      // Contacts (system-wide)
      (globalContactsAll || []).forEach(cnt => {
        if (cnt?.email) eSet.add(normalizeEmail(cnt.email));
    
        if (Array.isArray(cnt?.phoneNumbers)) {
          cnt.phoneNumbers.forEach(p => {
            if (p?.phoneNo) pSet.add(normalizePhone(p.phoneNo));
          });
        } else if (cnt?.phoneNo) {
          pSet.add(normalizePhone(cnt.phoneNo));
        }
      });
    
      setEmailIndex(eSet);
      setPhoneIndex(pSet);
    }, [allCustomers, globalContactsAll]);

    
    useEffect(() => {
      const next = { customerEmail: "", customerPhones: {}, contactEmail: "", contactPhones: {} };

      // Customer email
      if (customer?.email && isEmailTaken(customer.email)) {
        next.customerEmail = "This email is already taken.";
      }

      // Customer phones
      (customer?.phoneNumbers || []).forEach((p, idx) => {
        const ph = p?.phoneNo;
        if (ph && isPhoneTaken(ph)) {
          next.customerPhones[idx] = "This phone number is already taken.";
        }
      });

      // Contact email (only relevant in business flow where you collect primary contact)
      if (customer?.customerType === "business" && contact?.email && isEmailTaken(contact.email)) {
        next.contactEmail = "This email is already taken.";
      }

      // Contact phones
      (contact?.phoneNumbers || []).forEach((p, idx) => {
        const ph = p?.phoneNo;
        if (ph && isPhoneTaken(ph)) {
          next.contactPhones[idx] = "This phone number is already taken.";
        }
      });

      setDupeErrors(next);
    }, [customer?.email, customer?.phoneNumbers, contact?.email, contact?.phoneNumbers, customer?.customerType]);

    // any blocking duplicates?
    const hasBlockingDupes =
    !!dupeErrors.customerEmail ||
    Object.keys(dupeErrors.customerPhones).length > 0 ||
    (customer?.customerType === "business" && !!dupeErrors.contactEmail) ||
    (customer?.customerType === "business" && Object.keys(dupeErrors.contactPhones).length > 0);

    // guard next/save
    
    const handleNextWithDupeGuard = () => {
    // Only enforce on steps that capture emails/phones
    const stepCapturesIdentity = activeStep === 0 || activeStep === 2;
    if (stepCapturesIdentity && hasBlockingDupes) {
      toast?.error?.("Duplicate email/phone found. Please resolve before continuing.");
      return;
    }
    handleNext();
    };
 const handleStateInputChange = (field, value) => {
  const expanded = expandStateName(value);
  handleChange(field, expanded);
};


  const StepperHeaderContainer = styled(CardContent)(({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      borderRight: 0,
      borderBottom: 'none',
    },
  }));

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Fragment>
            <Box sx={{ 
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              gap: isSmallScreen ? 4 : 0
            }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: isSmallScreen ? "100%" : "50%",
                  pr: isSmallScreen ? 0 : 4
                }}
              >
                <Grid container spacing={5}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        required
                        sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                          "& .MuiFormLabel-asterisk": {
                            color: "error.main",
                          },
                          marginBottom: "4px",
                        }}
                      >
                        Customer Type
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="customerTypeLabel"
                        name="customerTypeSelection"
                        value={customer?.customerType}
                        onChange={(e) =>
                          handleChange("customerType", e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="individual"
                          control={<Radio color="success" />}
                          label="Individual"
                        />
                        <FormControlLabel
                          value="business"
                          control={<Radio color="success" />}
                          label="Business"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  {customer?.customerType === "individual" ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          required={customer?.customerType === "individual"}
                          fullWidth
                          placeholder="John"
                          label="First Name"
                          value={customer?.firstName}
                          // onChange={(e) => setCustomer((prevState) => ({ ...prevState, ['firstName']: e.target.value }))}
                          onChange={(e) =>
                            handleChange("firstName", e.target.value)
                          }
                          error={
                            customer?.customerType === "individual"
                              ? !!errors?.firstName
                              : false
                          }
                          helperText={
                            customer?.customerType === "individual"
                              ? errors?.firstName
                              : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          required={customer?.customerType === "individual"}
                          fullWidth
                          placeholder="Doe"
                          label="Last Name"
                          value={customer?.lastName}
                          onChange={(e) =>
                            handleChange("lastName", e.target.value)
                          }
                          error={
                            customer?.customerType === "individual"
                              ? !!errors?.lastName
                              : false
                          }
                          helperText={
                            customer?.customerType === "individual"
                              ? errors?.lastName
                              : ""
                          }
                        />
                      </Grid>
                      {/* {customer?.customerType === "business" && (
                  <Grid item xs={6}>
                    <CustomTextField
                      required
                      fullWidth
                      placeholder="Cleri"
                      label="Company Name"
                      value={customer?.companyName}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      error={!!errors?.companyName} 
                      helperText={errors?.companyName}
                    />
                  </Grid>
                )} */}
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          required
                          fullWidth
                          placeholder="name@example.com"
                          label="Email"
                          value={customer?.email}
                          onChange={(e) =>
                            handleChange("email", e.target.value)
                          }
                          error={!!(errors?.email || dupeErrors.customerEmail)}
                          helperText={errors?.email || dupeErrors.customerEmail}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12} 
                        sm={6}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <FormControlLabel
                          label="Mark as Lead"
                          control={
                            <Checkbox
                              name="basic-unchecked"
                              color="success"
                              checked={customer?.isLead}
                              onChange={(e) =>
                                handleChange("isLead", e.target.checked)
                              }
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormLabel
                          required
                          sx={{
                            display: "flex",
                            fontSize: "13px",
                            color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                            "& .MuiFormLabel-asterisk": {
                              color: "error.main",
                            },
                            marginBottom: "4px",
                          }}
                        >
                          Phone
                        </FormLabel>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Grid item xs={12}>
                            {(customer?.phoneNumbers).map((field, index) => (
                              <>
                                <Box
                                  key={field.phoneID || index} // Use phoneID as a unique key, fallback to index
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 2,
                                  }}
                                >
                                  {/* field number input */}
                                  <PhoneInput
                                    disableDropdown={true}
                                    disableCountryCode={true}
                                    required
                                    inputClass="custom-phone-input"
                                    placeholder="(000) 000-0000"
                                    label={`Phone # ${index + 1}`}
                                    country={"us"}
                                    onlyCountries={["us"]}
                                    isValid={isValidContactPhone[index]}
                                    regions={"america"}
                                    value={field.phoneNo || ""}
                                    onChange={(e) =>
                                      handlePhoneFieldChange(index, e)
                                    }
                                    copyNumbersOnly={true}
                                    jumpCursorToEnd={false}
                                    containerStyle={{
                                      width: "100%",
                                      margin: 0,
                                    }}
                                    inputStyle={{
                                      width: "100%",
                                      height: "39px",
                                      border: !isValidPhone[index]
                                      ? "1px solid #EA5455"
                                      : `1px solid ${theme.palette.divider}`,
                                      borderRadius: "6px",
                                      paddingLeft: "48px",
                                      backgroundColor: theme.palette.mode === 'dark' ? "#1B2431" : "#fff",
                                      color: theme.palette.mode === 'dark' ? "rgba(208, 212, 241, 0.68)" : "rgba(47, 43, 61, 0.68)",
                                    }}
                                  />

                                  {/* Primary marker */}
                                  <IconButton
                                    title="Mark as Primary"
                                    color="secondary"
                                    onClick={() => handleSetPrimary(index)}
                                    disabled={field.isPrimary}
                                  >
                                    {field.isPrimary ? (
                                      <StarIcon sx={{ color: "#E7D557" }} />
                                    ) : (
                                      <StarOutlineIcon />
                                    )}
                                  </IconButton>
                                  <IconButton
                                    title="Delete"
                                    color="secondary"
                                    onClick={() =>
                                      handleDeletePhoneField(index)
                                    }
                                    disabled={field.isPrimary}
                                  >
                                    <Icon icon="tabler:trash" />
                                  </IconButton>
                                </Box>
                                {phoneError[index] && (
                                  <FormHelperText
                                    sx={{
                                      color: "#EA5455",
                                      mt: "2px",
                                      mb: "6px",
                                      lineHeight: 1.154,
                                      fontSize: "0.8125rem",
                                      fontFamily:
                                        'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                                    }}
                                  >
                                    {phoneError[index]}
                                  </FormHelperText>
                                )}
                                {!phoneError[index] &&
                                  errors?.phoneError &&
                                  customer?.phoneNumbers[index].phoneNo ===
                                    "" && (
                                    <FormHelperText
                                      sx={{
                                        color: "#EA5455",
                                        mt: "2px",
                                        mb: "6px",
                                        lineHeight: 1.154,
                                        fontSize: "0.8125rem",
                                        fontWeight: 400,
                                        fontFamily:
                                          'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                                      }}
                                    >
                                      {errors?.phoneError}
                                    </FormHelperText>
                                  )}
                              </>
                            ))}
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={handleAddPhoneField}
                            >
                              Add Phone
                            </Button>
                          </Grid>
                        </Box>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          required
                          fullWidth
                          placeholder="Cleri"
                          label="Company Name"
                          value={customer?.companyName}
                          onChange={(e) =>
                            handleChange("companyName", e.target.value)
                          }
                          error={!!errors?.companyName}
                          helperText={errors?.companyName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          required
                          fullWidth
                          placeholder="Comany Email"
                          label="Company Email"
                          value={customer?.email}
                          onChange={(e) =>
                            handleChange("email", e.target.value)
                          }
                          error={!!(errors?.email || dupeErrors.customerEmail)}
                          helperText={errors?.email || dupeErrors.customerEmail}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Grid item xs={12}>
                            <FormLabel
                              required
                              sx={{
                                display: "flex",
                                fontSize: "13px",
                                color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                                "& .MuiFormLabel-asterisk": {
                                  color: "error.main",
                                },
                                marginBottom: "4px",
                              }}
                            >
                              Company Phone
                            </FormLabel>
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              <Grid item xs={12}>
                                {customer?.phoneNumbers &&
                                  (customer?.phoneNumbers).map(
                                    (field, index) => (
                                      <>
                                        <Box
                                          key={field.phoneID || index} // Use phoneID as a unique key, fallback to index
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 2,
                                          }}
                                        >
                                          {/* field number input */}
                                          <PhoneInput
                                            disableDropdown={true}
                                            disableCountryCode={true}
                                            required
                                            inputClass="custom-phone-input"
                                            placeholder="(000) 000-0000"
                                            label={`Phone # ${index + 1}`}
                                            country={"us"}
                                            onlyCountries={["us"]}
                                            isValid={isValidPhone[index]}
                                            regions={"america"}
                                            value={field.phoneNo || ""}
                                            onChange={(e) =>
                                              handlePhoneFieldChange(index, e)
                                            }
                                            copyNumbersOnly={true}
                                            jumpCursorToEnd={false}
                                            containerStyle={{
                                              width: "100%",
                                              margin: 0,
                                            }}
                                            inputStyle={{
                                              width: "100%",
                                              height: "39px",
                                              border: !isValidPhone[index]
                                                ? "1px solid #EA5455"
                                                : "1px solid  #ced4da",
                                              borderRadius: "6px",
                                              paddingLeft: "48px",
                                              backgroundColor: theme.palette.mode === 'dark' ? "#1B2431" : "#fff",
                                              color: theme.palette.mode === 'dark' ? "rgba(208, 212, 241, 0.68)" : "rgba(47, 43, 61, 0.68)",
                                            }}
                                          />

                                          {/* Primary marker */}
                                          <IconButton
                                            title="Mark as Primary"
                                            color="secondary"
                                            onClick={() =>
                                              handleSetPrimary(index)
                                            }
                                            disabled={field.isPrimary}
                                          >
                                            {field.isPrimary ? (
                                              <StarIcon
                                                sx={{ color: "#E7D557" }}
                                              />
                                            ) : (
                                              <StarOutlineIcon />
                                            )}
                                          </IconButton>
                                          <IconButton
                                            title="Delete"
                                            color="secondary"
                                            onClick={() =>
                                              handleDeletePhoneField(index)
                                            }
                                            disabled={field.isPrimary}
                                          >
                                            <Icon icon="tabler:trash" />
                                          </IconButton>
                                        </Box>
                                        {/* {phoneError[index] && (
                                          <FormHelperText
                                            sx={{
                                              color: "#EA5455",
                                              mt: "2px",
                                              mb: "6px",
                                              lineHeight: 1.154,
                                              fontSize: "0.8125rem",
                                              fontFamily:
                                                'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                                            }}
                                          >
                                            {phoneError[index]}
                                          </FormHelperText>
                                        )}
                                        {!phoneError[index] &&
                                          errors?.phoneError &&
                                          customer?.phoneNumbers[index]
                                            .phoneNo === "" && (
                                            <FormHelperText
                                              sx={{
                                                color: "#EA5455",
                                                mt: "2px",
                                                mb: "6px",
                                                lineHeight: 1.154,
                                                fontSize: "0.8125rem",
                                                fontWeight: 400,
                                                fontFamily:
                                                  'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                                              }}
                                            >
                                              {errors?.phoneError}
                                            </FormHelperText>
                                          )} */}
                                          {phoneError[index] ? (
                                            <FormHelperText sx={{ color: "#EA5455", mt: "2px", mb: "6px", lineHeight: 1.154 }}>
                                              {phoneError[index]}
                                            </FormHelperText>
                                          ) : dupeErrors.customerPhones[index] ? ( // ADD
                                            <FormHelperText sx={{ color: "#EA5455", mt: "2px", mb: "6px", lineHeight: 1.154 }}>
                                              {dupeErrors.customerPhones[index]}
                                            </FormHelperText>
                                          ) : (!phoneError[index] && errors?.phoneError && customer?.phoneNumbers[index].phoneNo === "") ? (
                                            <FormHelperText sx={{ color: "#EA5455", mt: "2px", mb: "6px", lineHeight: 1.154 }}>
                                              {errors?.phoneError}
                                            </FormHelperText>
                                          ) : null}
                                      </>
                                    )
                                  )}
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  onClick={handleAddPhoneField}
                                 
                                >
                                  Add Phone
                                </Button>
                              </Grid>
                            </Box>
                          </Grid>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme?.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(3)} !important`,
                          }}
                        >
                          Primary Contact
                        </Divider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          label="First Name"
                          placeholder="John"
                          required
                          fullWidth
                          name="firstName"
                          value={contact?.firstName}
                          onChange={(e) =>
                            handleContactChange("firstName", e.target.value)
                          }
                          error={!!errorContacts?.firstName}
                          helperText={errorContacts?.firstName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          label="Last Name"
                          placeholder="Doe"
                          required
                          fullWidth
                          name="lastName"
                          value={contact?.lastName}
                          onChange={(e) =>
                            handleContactChange("lastName", e.target.value)
                          }
                          error={!!errorContacts?.lastName}
                          helperText={errorContacts?.lastName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          label="Job Title"
                          placeholder="VP Facilities"
                          required
                          fullWidth
                          name="jobTitle"
                          value={contact?.jobTitle}
                          onChange={(e) =>
                            handleContactChange("jobTitle", e.target.value)
                          }
                          error={!!errorContacts?.jobTitle}
                          helperText={errorContacts?.jobTitle}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          label="Email"
                          placeholder="cleri@example.com"
                          required
                          fullWidth
                          name="email"
                          value={contact?.email}
                          onChange={(e) =>
                            handleContactChange("email", e.target.value)
                          }
                          error={!!(errorContacts?.email || dupeErrors.contactEmail)}
                          helperText={errorContacts?.email || dupeErrors.contactEmail}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Grid item xs={12}>
                            <FormLabel
                              required
                              sx={{
                                display: "flex",
                                fontSize: "13px",
                                color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                                "& .MuiFormLabel-asterisk": {
                                  color: "error.main",
                                },
                                marginBottom: "4px",
                              }}
                            >
                              Phone
                            </FormLabel>
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              <Grid item xs={12}>
                                {contact?.phoneNumbers &&
                                  (contact?.phoneNumbers).map(
                                    (field, index) => (
                                      <>
                                        <Box
                                          key={field.phoneID || index} // Use phoneID as a unique key, fallback to index
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 2,
                                          }}
                                        >
                                          {/* field number input */}
                                          <PhoneInput
                                            disableDropdown={true}
                                            disableCountryCode={true}
                                            required
                                            inputClass="custom-phone-input"
                                            placeholder="(000) 000-0000"
                                            label={`Phone # ${index + 1}`}
                                            country={"us"}
                                            onlyCountries={["us"]}
                                            isValid={isValidPhone[index]}
                                            regions={"america"}
                                            value={field.phoneNo || ""}
                                            onChange={(e) =>
                                              handleContactPhoneFieldChange(
                                                index,
                                                e
                                              )
                                            }
                                            copyNumbersOnly={true}
                                            jumpCursorToEnd={false}
                                            containerStyle={{
                                              width: "100%",
                                              margin: 0,
                                            }}
                                            inputStyle={{
                                              width: "100%",
                                              height: "39px",
                                              border: !isValidContactPhone[
                                                index
                                              ]
                                                ? "1px solid #EA5455"
                                                : "1px solid  #ced4da",
                                              borderRadius: "6px",
                                              paddingLeft: "48px",
                                              backgroundColor: theme.palette.mode === 'dark' ? "#1B2431" : "#fff",
                                              color: theme.palette.mode === 'dark' ? "rgba(208, 212, 241, 0.68)" : "rgba(47, 43, 61, 0.68)",
                                            }}
                                          />

                                          {/* Primary marker */}
                                          <IconButton
                                            title="Mark as Primary"
                                            color="secondary"
                                            onClick={() =>
                                              handleSetPrimaryContact(index)
                                            }
                                            disabled={field.isPrimary}
                                          >
                                            {field.isPrimary ? (
                                              <StarIcon
                                                sx={{ color: "#E7D557" }}
                                              />
                                            ) : (
                                              <StarOutlineIcon />
                                            )}
                                          </IconButton>
                                          <IconButton
                                            title="Delete"
                                            color="secondary"
                                            onClick={() =>
                                              handleDeletePhoneFieldContact(
                                                index
                                              )
                                            }
                                            disabled={field.isPrimary}
                                          >
                                            <Icon icon="tabler:trash" />
                                          </IconButton>
                                        </Box>
                                        {/* {contactPhoneError[index] && (
                                          <FormHelperText
                                            sx={{
                                              color: "#EA5455",
                                              mt: "2px",
                                              mb: "6px",
                                              lineHeight: 1.154,
                                              fontSize: "0.8125rem",
                                              fontFamily:
                                                'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                                            }}
                                          >
                                            {contactPhoneError[index]}
                                          </FormHelperText>
                                        )}
                                        {!contactPhoneError[index] &&
                                          errorContacts.contactPhoneError &&
                                          contact?.phoneNumbers[index]
                                            .phoneNo === "" && (
                                            <FormHelperText
                                              sx={{
                                                color: "#EA5455",
                                                mt: "2px",
                                                mb: "6px",
                                                lineHeight: 1.154,
                                                fontSize: "0.8125rem",
                                                fontWeight: 400,
                                                fontFamily:
                                                  'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                                              }}
                                            >
                                              {errorContacts.contactPhoneError}
                                            </FormHelperText>
                                          )} */}
                                          {contactPhoneError[index] ? (
                                            <FormHelperText sx={{ color: "#EA5455", mt: "2px", mb: "6px", lineHeight: 1.154 }}>
                                              {contactPhoneError[index]}
                                            </FormHelperText>
                                          ) : dupeErrors.contactPhones[index] ? ( // ADD
                                            <FormHelperText sx={{ color: "#EA5455", mt: "2px", mb: "6px", lineHeight: 1.154 }}>
                                              {dupeErrors.contactPhones[index]}
                                            </FormHelperText>
                                          ) : (!contactPhoneError[index] && errorContacts.contactPhoneError && contact?.phoneNumbers[index].phoneNo === "") ? (
                                            <FormHelperText sx={{ color: "#EA5455", mt: "2px", mb: "6px", lineHeight: 1.154 }}>
                                              {errorContacts.contactPhoneError}
                                            </FormHelperText>
                                          ) : null}

                                      </>
                                    )
                                  )}
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  onClick={handleAddPhoneFieldContact}
                                >
                                  Add Phone
                                </Button>
                              </Grid>
                            </Box>
                          </Grid>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
              <Box>
                <Divider
                  orientation="vertical"
                  sx={{ height: "100%", mx: 6 }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: isSmallScreen ? "100%" : "50%",
                  pt: isSmallScreen ? 4 : 0,
                  pl: isSmallScreen ? 0 : 4
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Divider
                      sx={{
                        color: "text.disabled",
                        "& .MuiDivider-wrapper": { px: 6 },
                        fontSize: theme?.typography.body2.fontSize,
                        my: (theme) => `${theme.spacing(3)} !important`,
                      }}
                    >
                      {customer?.customerType === "individual"
                        ? "Main Address"
                        : "Company Address"}
                    </Divider>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <CustomTextField
                        fullWidth
                        placeholder="Street 1"
                        value={customer?.companyAddress_Street1}
                        onChange={(e) =>
                          handleChange("companyAddress_Street1", e.target.value)
                        }
                        error={!!errors?.companyAddress_Street1}
                        helperText={errors?.companyAddress_Street1}
                      />

                      <CustomTextField
                        fullWidth
                        placeholder="Street 2"
                        value={customer?.companyAddress_Street2}
                        onChange={(e) =>
                          handleChange("companyAddress_Street2", e.target.value)
                        }
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      placeholder="City"
                      value={customer?.companyAddress_City}
                      onChange={(e) =>
                        handleChange("companyAddress_City", e.target.value)
                      }
                      error={!!errors?.companyAddress_City}
                      helperText={errors?.companyAddress_City}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                  <CustomAutocomplete
                    options={stateNames}
                    id="autocomplete-custom"
                    value={
                      stateNames.find(option => option === customer?.companyAddress_State) || null
                    }
                    onInputChange={(event, newInputValue) =>
                      handleStateInputChange("companyAddress_State", newInputValue)
                    }
                    onChange={(event, newValue) =>
                      handleStateInputChange("companyAddress_State", newValue)
                    }
                    getOptionLabel={(option) => option || ""}
                    renderInput={(params) => (
                      <CustomTextField
                        fullWidth
                        placeholder="State"
                        {...params}
                        error={!!errors?.companyAddress_State}
                        helperText={errors?.companyAddress_State}
                      />
                    )}
                  />

                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      placeholder="Zip code"
                      value={customer?.companyAddress_ZipCode}
                      onChange={(e) =>
                        handleChange("companyAddress_ZipCode", e.target.value)
                      }
                      error={!!errors?.companyAddress_ZipCode}
                      helperText={errors?.companyAddress_ZipCode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      placeholder="County"
                      value={customer?.companyAddress_County}
                      onChange={(e) =>
                        handleChange("companyAddress_County", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                   <CustomAutocomplete
                    options={[...timezones].sort((a, b) =>
                      a.label.localeCompare(b.label)
                    )}
                    value={
                      timezones.find(
                        (option) => option.value === customer?.timeZone
                      ) || null
                    }
                    onChange={(event, newValue) =>
                      handleChange("timeZone", newValue?.value || "")
                    }
                    getOptionLabel={(option) => option?.label || ""}
                    renderInput={(params) => (
                      <CustomTextField
                        label="Timezone"
                        fullWidth
                        placeholder="EST - Eastern Standard Time"
                        {...params}
                      />
                    )}
                  />
                  </Grid>
                 <Grid item xs={12} sm={6}>
                <CustomAutocomplete
                  options={[...allCustomers].sort((a, b) =>
                    (
                      (a.firstName && a.lastName
                        ? `${a.firstName} ${a.lastName}`
                        : a.companyName || ""
                      ).toLowerCase()
                    ).localeCompare(
                      (b.firstName && b.lastName
                        ? `${b.firstName} ${b.lastName}`
                        : b.companyName || ""
                      ).toLowerCase()
                    )
                  )}
                  value={
                    allCustomers.find(
                      (option) => option.customerID === customer?.referredBy
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    handleChange("referredBy", newValue?.customerID || null)
                  }
                  getOptionLabel={(option) =>
                    option?.firstName || option?.lastName
                      ? `${option?.firstName ?? ""} ${option?.lastName ?? ""}`.trim()
                      : option?.companyName ?? ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?.customerID === value?.customerID
                  }
                  renderInput={(params) => (
                    <CustomTextField
                      label="Referred By"
                      fullWidth
                      placeholder="Search or Select Referral"
                      {...params}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                 <Autocomplete
                    options={sortedEmployees}
                    value={
                      sortedEmployees.find(
                        (option) => option.employeeID === customer?.soldBy
                      ) || null
                    }
                    onChange={(event, newValue) =>
                      handleChange("soldBy", newValue?.employeeID || null)
                    }
                    getOptionLabel={(option) =>
                      `${option?.firstName ?? ""} ${option?.lastName ?? ""}`.trim()
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.employeeID === value?.employeeID
                    }
                    disableListWrap
                    ListboxProps={{
                      style: { maxHeight: 300 },
                    }}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Sold By"
                        fullWidth
                        placeholder="Search or Select Employee"
                      />
                    )}
                  />

                </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label={`Billing address is the same as ${
                        customer?.customerType === "individual"
                          ? "Main Address"
                          : "Company Address"
                      }`}
                      control={
                        <Checkbox
                          checked={customer?.billingAddress}
                          onChange={(e) =>
                            handleChange("billingAddress", e.target.checked)
                          }
                          name="basic-unchecked"
                          color="success"
                        />
                      }
                    />
                  </Grid>
                  {!customer?.billingAddress && (
                    <>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme?.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(3)} !important`,
                          }}
                        >
                          Billing Address
                        </Divider>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          <CustomTextField
                            fullWidth
                            placeholder="Street 1"
                            value={customer?.billingAddress_Street1}
                            onChange={(e) =>
                              handleChange(
                                "billingAddress_Street1",
                                e.target.value
                              )
                            }
                              error={!!errors.billingAddress_Street1}
                              helperText={errors.billingAddress_Street1}
                          />

                          <CustomTextField
                            fullWidth
                            placeholder="Street 2"
                            value={customer?.billingAddress_Street2}
                            onChange={(e) =>
                              handleChange(
                                "billingAddress_Street2",
                                e.target.value
                              )
                            }
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <CustomTextField
                          fullWidth
                          placeholder="City"
                          value={customer?.billingAddress_City}
                          onChange={(e) =>
                            handleChange("billingAddress_City", e.target.value)
                          }
                           error={!!errors.billingAddress_City}
                            helperText={errors.billingAddress_City}
                        />
                      </Grid>
                          <Grid item xs={6}>
                            <CustomAutocomplete
                              options={stateNames}
                              id="autocomplete-billing-state"
                              value={
                                stateNames.find(option => option === customer?.billingAddress_State) || null
                              }
                              onInputChange={(event, newInputValue) =>
                                handleStateInputChange("billingAddress_State", newInputValue)
                              }
                              onChange={(event, newValue) =>
                                handleStateInputChange("billingAddress_State", newValue)
                              }
                              getOptionLabel={(option) => option || ""}
                              renderInput={(params) => (
                                <CustomTextField
                                  fullWidth
                                  placeholder="State"
                                  {...params}
                                  error={!!errors.billingAddress_State}
                                  helperText={errors.billingAddress_State}
                                />
                              )}
                            />
                          </Grid>
                      <Grid item xs={6}>
                        <CustomTextField
                          fullWidth
                          placeholder="Zip code"
                          value={customer?.billingAddress_ZipCode}
                          onChange={(e) =>
                            handleChange(
                              "billingAddress_ZipCode",
                              e.target.value
                            )
                          }
                           error={!!errors.billingAddress_ZipCode}
                           helperText={errors.billingAddress_ZipCode}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <CustomTextField
                          fullWidth
                          placeholder="County"
                          value={customer?.billingAddress_County}
                          onChange={(e) =>
                            handleChange(
                              "billingAddress_County",
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Box>
          </Fragment>
        );
      case 1: 
      return (
        <BillingInformation
          // estTravelTime={estTravelTime}
          // setEstTravelTime={setEstTravelTime}
          billingFreq={billingFreq}
          recurringType={recurringType}          
          depositReq={depositReq}
          setDepositReq={setDepositReq}
          depositType={depositType}
          setDepositType={setDepositType}
          formatsBilling={formatsBilling}
          setFormatsBilling={setFormatsBilling}
          monthlyOptions={monthlyOptions}        
          setMonthlyOptions={setMonthlyOptions}
          // occurrences={occurrences}
          // setOccurrences={setOccurrences}
          cycleStartDate={cycleStartDate}
          setCycleStartDate={setCycleStartDate}
          intervalDaysInput={intervalDaysInput}
          setIntervalDaysInput={setIntervalDaysInput}
          weekOfMonth={weekOfMonth}
          setWeekOfMonth={setWeekOfMonth}
          quarter={quarter}
          setQuarter={setQuarter}
          monthOfQuarter={monthOfQuarter}
          setMonthOfQuarter={setMonthOfQuarter}
          // handleEstTravelTimeChange={handleEstTravelTimeChange}
          handleBillingFreqChange={handleBillingFreqChange}
          handleRecurringTypeChange={handleRecurringTypeChange}
          handledDepositReqChange={handledDepositReqChange}
          handleDepositTypeChange={handleDepositTypeChange}
          handleFormatBilling={handleFormatBilling}
          setIsChanged={setIsChanged}         
          setBillingInfoChanged={setBillingInfoChanged}
        />
      );
      case 2:
        return (
          <Fragment key={step}>
            <Box sx={{
              display: 'flex', 
              justifyContent: isXSmallScreen ? 'flex-start' : 'flex-end', 
              alignItems: 'center', 
              width: '100%',
              mb: 6,
              flexDirection: isXSmallScreen ? 'column' : 'row',
              gap: isXSmallScreen ? 2 : 4
            }}>
              <CustomTextField 
                placeholder="Search Contact"
                value={searchContactText}
                onChange={(e) => handleContactSearchChange(e.target.value)} 
                sx={{ 
                  width: isXSmallScreen ? '100%' : 'auto',
                  minWidth: isXSmallScreen ? '100%' : 'auto'
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon icon='tabler:search' />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchContactText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearContactSearch}
                          aria-label="clear"
                          sx={{mr: 2}}
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{
                display: 'flex',
                gap: 4,
                width: isXSmallScreen ? '100%' : 'auto',
                flexDirection: isXSmallScreen ? 'column' : 'row'
              }}>
                <Button 
                  variant="outlined"
                  color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                  onClick={()=>{handleOpenBulkImportContactDlg()}}
                  fullWidth={isXSmallScreen}
                >
                  Bulk Import
                </Button>
                <Button
                  variant="contained"
                  color="main"
                  startIcon={<AddIcon />}
                  onClick={handleOpenNewContactDlg}
                  fullWidth={isXSmallScreen}
                >
                  New Contact
                </Button>
              </Box>
            </Box>
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <DataGridPro
                  autoHeight
                  rows={filteredContactRows}
                  rowHeight={62}                  
                  columns={columnsAddContact}
                  getRowId={(row) => row.id}
                  pageSizeOptions={[5, 10, { value: filteredContactRows.length, label: 'Show All'}]}
                  paginationModel={paginationModelAddContact}
                  onPaginationModelChange={handleContactPaginationModel}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textTransform: "none",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Fragment>
        );
      case 3:
        return (
          <Fragment key={step}>
            <Box sx={{
              display: 'flex', 
              justifyContent: isXSmallScreen ? 'flex-start' : 'flex-end', 
              alignItems: 'center', 
              width: '100%',
              mb: 6,
              flexDirection: isXSmallScreen ? 'column' : 'row',
              gap: isXSmallScreen ? 2 : 0
            }}>
              <CustomTextField 
                placeholder="Search Jobsite"
                value={searchJobsiteText}
                onChange={(e) => handleJobsiteSearchChange(e.target.value)}  
               sx={{
                width: isXSmallScreen ? '100%' : 'auto',
                minWidth: isXSmallScreen ? '100%' : 'auto',
                mr:2
              }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon icon='tabler:search' />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchJobsiteText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearJobsiteSearch}
                          aria-label="clear"
                          sx={{mr: 2}}
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{
                display: 'flex',
                gap: 2,
                width: isXSmallScreen ? '100%' : 'auto',
                flexDirection: isXSmallScreen ? 'column' : 'row'
              }}>
                <Button 
                  variant="outlined" 
                  color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                  onClick={()=>{handleOpenBulkImportJobsiteDlg()}}
                  fullWidth={isXSmallScreen}
                >
                  Bulk Import
                </Button>
                <Button
                  variant="contained"
                  color="main"
                  startIcon={<AddIcon />}
                  onClick={handleOpenNewJobsiteDlg}
                  fullWidth={isXSmallScreen}
                >
                  New Jobsite
                </Button>
              </Box>
            </Box>
            {allJobsites && allJobsites.length > 0 ? 
            allJobsites
            .filter(row =>    
              Object.values(row).some(value =>
                  value && value.toString().toLowerCase().includes(searchJobsiteText.toLowerCase())
              )
            )
            .map((jobsite) =>(
              <ListItem
                sx={{ 
                  borderBottom: '1px solid', 
                  borderColor: '#D9D9D9', 
                  mb: 4,
                  flexDirection: isXSmallScreen ? 'column' : 'row',
                  alignItems: isXSmallScreen ? 'flex-start' : 'center',
                  gap: isXSmallScreen ? 2 : 0
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: isXSmallScreen ? '100%' : 'auto'
                }}>
                  <ListItemIcon color='error'>
                    <CorporateFareIcon />
                  </ListItemIcon>
                  <ListItemText 
                    sx={{
                      fontWeight: 550, 
                      ml: 1,
                      mr:3,
                      '& .MuiTypography-root': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isXSmallScreen ? '200px' : 'none'
                      }
                    }} 
                    primary={
                      <Typography variant="h6" fontWeight={550}>{jobsite.jobsiteName}</Typography>
                    } 
                     secondary={
                          <div>
                            <div>{jobsite.street1}</div>
                            <div>{jobsite.street2}</div>
                          </div>
                        }                  
                  />
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  ml: 'auto',
                  gap: 1,
                }}>
                  <CustomChip 
                    label={jobsite?.jobsiteType} 
                    skin='light' 
                    color={jobsite.jobsiteType === 'Residential' ? 'info' : 'warning'} 
                    sx={{ 
                      mr: 2,
                      width: isXSmallScreen ? '100px' : 'auto'
                    }} 
                  />
                  <IconButton onClick={()=>handleQuickViewOpen(jobsite.jobsiteID)}>
                    <Icon icon='tabler:eye' />
                  </IconButton>
                  <IconButton onClick={()=>handleJobsiteEdit(jobsite.jobsiteID)}>
                    <Icon icon='tabler:pencil' />
                  </IconButton>
                  <IconButton color='error' onClick={()=>handleJobsiteDelete(jobsite.jobsiteID,jobsite.jobsiteName)}>
                    <Icon icon='tabler:trash' />
                  </IconButton>
                </Box>
              </ListItem>
            )):(
              <Card
                sx={{
                  width: '100%',
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    textAlign: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    backgroundColor: "#FBFBFC",
                    p: (theme) =>
                      `${theme.spacing(9.75, 5, 9.25)} !important`,
                  }}
                >
                  <CustomAvatar skin="light" sx={{ mb: 2.25 }}>
                    <CorporateFareIcon />
                  </CustomAvatar>
                  <Typography variant="h4" sx={{ mb: 2.75 }}>
                    No Jobsites added yet
                  </Typography>
                  <Typography sx={{ mb: 6, color: "text.secondary" }}>
                    Add jobsites where services will be requested in the
                    future.
                  </Typography>
                </CardContent>
              </Card>
            )}  
          </Fragment>
        );
      default:
        return "Unknown Step";
    }
  };

  const memoizedSteps = useMemo(() => {
    return steps.map((step, index) => {
      const RenderAvatar = activeStep >= index ? CustomAvatar : Avatar;
      const isStepActive = activeStep === index;
  
      return (
        <StepJobsite key={index} sx={{ paddingBottom: 0 }}>
          <StepLabel StepIconComponent={StepperCustomDot}>
            <div className="step-label">
              <RenderAvatar
                variant="rounded"
                {...(activeStep >= index && {
                  skin: "light",
                })}
                {...(isStepActive && { skin: "filled" })}
                {...(activeStep >= index && {
                  color: "success",
                })}
                sx={{
                  ...(isStepActive && {
                    boxShadow: (theme) => theme.shadows[3],
                    color: "#fff", // white when active
                  }),
                  ...(activeStep > index && {
                    color: (theme) => theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.48)' : hexToRGBA(theme.palette.primary.main, 0.4),
                  }),
                }}
              >
                <Icon icon={step.icon} />
              </RenderAvatar>
              <div>
                <Typography 
                  variant="subtitle2"
                  color="text.secondary"
                  {...(activeStep === index && {
                    fontWeight: "550",
                    color: "text.primary",
                  })}
                >
                  {step.title}
                </Typography>
              </div>
            </div>
          </StepLabel>
        </StepJobsite>
      );
    });
  }, [steps, activeStep]); 

    return (   
      <>                 
        <Dialog
          fullWidth={true}
          maxWidth="lg"
          open={open}
          PaperComponent={DraggablePaper}
          onClose={handleClose}
          aria-labelledby="form-dialog-title"
          PaperProps={{
            sx: {
              height: '100%', 
              maxHeight: '95vh',
              width: isSmallScreen ? '95vw' : 'auto',
              m: isSmallScreen ? 2 : 0
            },
          }}
          sx={{
            '& .MuiDialog-container': {
              alignItems: isSmallScreen ? 'flex-end' : 'center'
            }
          }}
        >
          <DialogTitle 
            style={{ 
              cursor: isSmallScreen ? 'default' : 'move', 
              borderBottom: '1px solid #D9D9D9',
              padding: isSmallScreen ? theme.spacing(2) : theme.spacing(4)
            }} 
            id="draggable-dialog-title"
          >
            <Box 
              sx={{
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexDirection: isSmallScreen ? 'column' : 'row',
                gap: isSmallScreen ? 2 : 0
              }}
            >
              <Typography variant="h3" fontWeight='bold'>Create Customer</Typography>
              <StepperHeaderContainer sx={{
                  width: isSmallScreen ? '100%' : 'auto',
                  pt: isSmallScreen ? 2 : 0
                }}>
                <StepperWrapper>
                 <Stepper
  activeStep={activeStep}
  connector={<></>}
  sx={{
    justifyContent: "space-between",
    flexWrap: isSmallScreen ? 'wrap' : 'nowrap',
    paddingTop: isSmallScreen ? '0' : '1.5rem !important',
    gap: isSmallScreen ? 2 : 0 // adds spacing between wrapped steps
  }}
>
  {memoizedSteps}
</Stepper>

                </StepperWrapper>
              </StepperHeaderContainer>
            </Box>
          </DialogTitle>
          <DialogContent sx={{
              px: isSmallScreen ? 2 : 6,
              pt: isSmallScreen ? 2 : 4,
              pb: isSmallScreen ? 2 : 0
            }}>
            <Box sx={{
                mx: isSmallScreen ? 2 : 6,
                mt: isSmallScreen ? 2 : 4,
                mb: isSmallScreen ? 2 : 0
              }}>
              <form onSubmit={(e) => e.preventDefault()}>
                <Grid container spacing={5}>
                  {getStepContent(activeStep)}
                </Grid>
              </form>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ 
              display: "flex", 
              justifyContent: "space-between", 
              padding: isSmallScreen ? '16px !important' : '20px 24px !important', 
              boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`, 
              zIndex: 1,
              flexDirection: isSmallScreen ? 'column-reverse' : 'row',
              gap: isSmallScreen ? 2 : 0
            }}
              className="dialog-actions-dense"
          >
            <Box sx={{ width: isSmallScreen ? '100%' : 'auto' }}>
              <Button
                variant="contained"
                color="info"
                onClick={() => handleClose(false)}
                fullWidth={isSmallScreen}
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ 
                display: 'flex',
                width: isSmallScreen ? '100%' : 'auto',
                gap: isSmallScreen ? 2 : 0,
                flexDirection: isSmallScreen ? 'column' : 'row'
              }}>
              <Button
                variant="outlined"
                color="main"
                disabled={activeStep === 0}
                onClick={handleBack}
                fullWidth={isSmallScreen}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="main"
                sx={{ ml: isSmallScreen ? 0 : 5 }}
                // onClick={handleNext}
                onClick={handleNextWithDupeGuard}
                fullWidth={isSmallScreen}
              >
                {activeStep === steps.length - 1
                  ? "Save Customer"
                  : "Next Step"}
              </Button>
            </Box>
          </DialogActions>
          {quickViewOpen && (
            <QuickView
              handleQuickViewClose = {handleQuickViewClose }           
              jobsite={jobsite}
              contacts={contacts}
              propertyAreaList={propertyAreaList}
              alertList = {alertList}
              setAlertList = {setAlertList} 
            />
          )}
        </Dialog>

        {/* Add Contact Dialog */}
        <AddContactDialog 
          open={openNewContactDlg}
          handleClose={handleCloseNewContactDlg}
          customer = {customer}
          setLoading={setLoading}
          fetchContactData = {fetchContacts}   
          toast = {toast}                 
          contacts = {allContacts}       
        />

        <UpdateContactDialog 
          open={openUpdateContactDlg}
          handleClose={handleCloseUpdateDlg}
          customer = {customer}
          contactID = {contactID}
          fetchContactData = {fetchContacts}
          setContactID = {setContactID}
          toast = {toast}
          setLoading = {setLoading}        
          contacts = {allContacts}
        />      

        <BulkImport
          open={openBulkImportDlg}                
          handleClose={handleCloseBulkImportDlg}
          content={BulkImportContent}  
          files = {EmpFiles}
          loading = {btnLoader}
          getRootProps =  {getRootProps}
          getInputProps = {getInputProps}
          setFiles = {setEmpFiles}
          setLoading = {setLoading}
          navigate = {navigate}
          fetchContactData = {fetchContacts}
          fetchJobsiteData = {fetchJobsites}
        /> 

      {/* Create Jobsite Dialog */}       

        <AddJobsiteDialog 
          open={openNewJobsiteDlg}
          handleClose={handleCloseNewJobsiteDlg}
          setJobsiteOpen = {setOpenNewJobsiteDlg}
          customer = {customer}
          fetchContactData = {fetchContacts}
          toast = {toast}
          jobsite = {jobsite}
          handleJobsiteChange = {handleJobsiteChange}      
          navigate = {navigate} 
          NProgress = {NProgress}
          fetchContacts = {fetchContacts}  
          fetchJobsites = {fetchJobsites} 
          contacts = {allContacts}    
          propertyArea = {propertyArea}
          setPropertyArea = {setPropertyArea}
          areaMeasurement = {areaMeasurement} 
          setAreaMeasurement = {setAreaMeasurement}
          setLoading = {setLoading}
          activeStep={activeStepJobsite}
          setActiveStep={setActiveStepJobsite}
          clearAreaMeasurement={clearAreaMeasurement}
          fetchPropertyArea = {fetchPropertyArea}
          propertyAreaList = {propertyAreaList}
          clearPropertyArea = {clearPropertyArea}
          jobsiteContent = {jobsiteContent}
          alertList = {alertList}
          setAlertList = {setAlertList}
          files = {files} 
          setFiles={setFiles}
        />       
      </>
    );
  }

export default AddCustomerDialog;